import { Header, HeaderProps } from '@almbrand/header';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import { PageContext } from 'context/PageContext';
import { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { removeStateFromSessionStorage } from 'utilities';
import pskLogoLight from './assets/psk-logo-light.svg';
import styles from './HeaderComponent.module.scss';

interface BasketIconProps<T> {
	basketIcon?: HeaderProps<T>['basketIcon'];
}

export const HeaderComponent: React.FC<HeaderReference & BasketIconProps<any> & { isPackagesPage?: boolean }> = ({
	basketIcon,
	isPackagesPage,
	logoImage,
	logoUrl,
	loginLabelText = 'Login',
	loginUrl,
	logoutUrl,
	loginButtonType,
	headerLinks,
	iconPosition,
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};
	const { isMobile } = useDeviceQuery();
	const customer = useAppSelector((state) => state.customer);

	// Theme specific settings
	const isPrivatsikring = theme === 'Privatsikring-theme';
	const isPrivatsikringPackagePage = isPackagesPage && isPrivatsikring;
	const shouldShowLockIcon = (theme === 'Codan-theme' && isMobile) || isPrivatsikring;

	const mapNavigationLinks = (links: Link[]) =>
		links
			.map((link) => ({
				text: link?.text ?? '',
				title: link?.title ?? '',
				url: link?.url ?? '',
				target: link?.target ?? '',
				isExternalLink: link?.isExternalLink ?? false,
				useVueRoute: link?.useVueRoute ?? false,
			}))
			.filter((link) => link.text && link.url);

	const handleLogout = () => {
		removeStateFromSessionStorage();
		location.href = logoutUrl?.url ?? '/auth/logout';
	};

	// Handle login/logout button based on customer data and theme
	const isCustomerDataReady = customer.isDataFetched && customer.customerProductsFetched;
	const getAuthButtonProps = (): HeaderProps<any>['buttonProps'] => ({
		buttonType: loginButtonType as HeaderProps<any>['buttonProps']['buttonType'],
		children: customer.isLoggedIn ? 'Log ud' : loginLabelText,
		size: 'small',
		...(shouldShowLockIcon
			? {
					iconPosition: iconPosition ?? 'left',
					iconProps: {
						themeName: theme,
						icon: customer.isLoggedIn ? 'lock_open' : 'lock_closed',
						width: '24',
						height: '24',
					},
				}
			: {}),
		...(customer.isLoggedIn
			? { onClick: handleLogout }
			: {
					linkButtonSettings: {
						href: loginUrl?.url ?? '/auth/login',
						target: loginUrl?.target ?? '_self',
					},
				}),
	});
	const authButton: HeaderProps<any>['buttonProps'] = isCustomerDataReady ? getAuthButtonProps() : null;

	return (
		<div className={styles.HeaderComponent}>
			<Header
				logoProps={{
					logoUrl: isPrivatsikringPackagePage ? pskLogoLight : (logoImage?.url ?? ''), // variable
					link: logoUrl?.url ?? '/',
					width: isMobile && isPrivatsikring ? '140px' : 'auto',
				}}
				navigationLinks={headerLinks?.length > 0 && mapNavigationLinks(headerLinks)}
				buttonProps={authButton}
				basketIcon={basketIcon}
				renderChildren={(link): JSX.Element => (
					<NavLink
						key={link?.url}
						to={link?.url}
						title={link?.title}
					>
						{link?.text}
					</NavLink>
				)}
			/>
		</div>
	);
};
